import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import './App.css';
import Postes from './Postes';
import HistoryPage from './HistoryPage';
import AdminPage from './AdminPage';
import { useNavigate } from 'react-router-dom';
import logo from './logoBranca.png';
import DOMPurify from 'dompurify';
import { FadeIn, AnimatedButton, TextAnimate } from './components/magicui';
import logger from './utils/logger';

function Login({ setIsLoggedIn, onLoginSuccess }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false); // Estado para controle de carregamento
    const navigate = useNavigate();

    // Função para validar o email
    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Função para validar a senha
    const validatePassword = (password) => {
        return password.length >= 6; // A senha deve ter pelo menos 6 caracteres
    };

    // Função de validação geral
    const validateLogin = (email, password) => {
        if (!validateEmail(email)) {
            return 'Email inválido.';
        }
        if (!validatePassword(password)) {
            return 'A senha deve ter pelo menos 6 caracteres.';
        }
        return null; // Sem erros
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true); // Inicia o carregamento

        // Sanitiza as entradas
        const sanitizedEmail = DOMPurify.sanitize(email);
        const sanitizedPassword = DOMPurify.sanitize(password);

        // Validação de entrada
        const validationError = validateLogin(sanitizedEmail, sanitizedPassword);
        if (validationError) {
            setError(validationError);
            setSuccessMessage('');
            setLoading(false); // Finaliza o carregamento
            return;
        }

        // Simula um tempo mínimo de carregamento para melhor UX
        const loginPromise = supabase.auth.signInWithPassword({ email: sanitizedEmail, password: sanitizedPassword });
        const minLoadingPromise = new Promise(resolve => setTimeout(resolve, 800));
        
        // Aguarda tanto o login quanto o tempo mínimo
        const [{ data, error }] = await Promise.all([loginPromise, minLoadingPromise]);

        setLoading(false); // Finaliza o carregamento

        logger.debug("Login attempt completed");

        if (error) {
            setError(error.message);
            setSuccessMessage('');
        } else if (data?.user) {
            logger.debug("User authenticated successfully");
            setSuccessMessage('Login efetuado com sucesso!');
            setError(null);
            setIsLoggedIn(true);
            onLoginSuccess(data.user); // Passa o usuário autenticado para a função de sucesso
            navigate('/postes');
        } else {
            setError('Erro desconhecido ao efetuar o login.');
        }
    };

    return (
        <div className="login-page">
            <FadeIn delay={0} className="login-container relative z-10">
                <FadeIn delay={1} className="login-header">
                    <div className="relative">
                        <img src={logo} alt="Logo da Empresa" className="login-logo relative z-10" />
                    </div>
                    <TextAnimate animation="slideUp" className="login-title">
                        Bem-vindo
                    </TextAnimate>
                    <FadeIn delay={2} className="login-subtitle">
                        Acesse o Gerador de NS
                    </FadeIn>
                </FadeIn>

                <FadeIn delay={3} className="login-form-wrapper">
                    <form onSubmit={handleLogin} className="login-form">
                        <FadeIn delay={4} className="form-group">
                            <input
                                type="email"
                                placeholder="Seu email corporativo"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                required
                                className="login-input"
                                aria-label="Email"
                            />
                        </FadeIn>

                        <FadeIn delay={5} className="form-group">
                            <input
                                type="password"
                                placeholder="Sua senha"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="login-input"
                                aria-label="Senha"
                            />
                        </FadeIn>

                        <FadeIn delay={6}>
                            <AnimatedButton
                                type="submit"
                                className={`login-button ${loading ? 'login-loading' : ''}`}
                                disabled={loading}
                                aria-live="polite"
                            >
                                {loading ? 'Autenticando...' : 'ENTRAR'}
                            </AnimatedButton>
                        </FadeIn>
                    </form>
                </FadeIn>

                <FadeIn delay={7} className="cadastro-message">
                    Caso não possua um cadastro, entre em contato com a T.I PE
                </FadeIn>

                {error && <FadeIn delay={8}><div className="error-message" role="alert">{error}</div></FadeIn>}
                {successMessage && <FadeIn delay={8}><div className="success-message" role="alert">{successMessage}</div></FadeIn>}
            </FadeIn>
        </div>
    );
}

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento inicial
    const [userRole, setUserRole] = useState(null); // Estado para armazenar o papel do usuário
    const [userRegionals, setUserRegionals] = useState([]); // Estado para armazenar as regionais
    const [userId, setUserId] = useState(null); // Estado para armazenar o ID do usuário

    const getUserRoleAndRegionals = useCallback(async (email) => {
        // Busca o perfil do usuário usando o email
        const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('role, regionals, active')
            .eq('email', email) // Aqui usamos o email do usuário
            .single();

        if (profileError) {
            logger.error("Erro ao obter perfil do usuário");
            return;
        }

        logger.debug("Profile data loaded successfully");

        // Verificar se o usuário está ativo
        if (!profileData.active) {
            logger.warn("Usuário inativo detectado");
            // Fazer logout do usuário inativo
            await supabase.auth.signOut();
            setIsLoggedIn(false);
            setUserRole(null);
            setUserRegionals([]);
            setUserId(null);
            throw new Error('Usuário desabilitado. Entre em contato com a T.I.');
        }

        // Armazena o papel e as regionais do usuário
        setUserRole(profileData.role);
        setUserRegionals(profileData.regionals || []);
    }, []);

    const handleLoginSuccess = useCallback(async (user) => {
        logger.debug("Login success handler called");
        if (user && user.email) {
            logger.debug("Processing user profile");
            try {
                await getUserRoleAndRegionals(user.email); // Chama a função para buscar o papel e as regionais após o login
            } catch (error) {
                logger.error("Erro ao verificar perfil do usuário:", error.message);
                // Exibir erro para o usuário
                alert(error.message || 'Erro ao verificar permissões do usuário.');
            }
        } else {
            logger.error("Usuário ou email não definido");
        }
    }, [getUserRoleAndRegionals]);

    // Função para fazer logout
    const handleLogout = useCallback(async () => {
        try {
            await supabase.auth.signOut();
            setIsLoggedIn(false);
            setUserRole(null);
            setUserRegionals([]);
            setUserId(null);
        } catch (error) {
            logger.error('Erro ao fazer logout:', error.message);
        }
    }, []);

    useEffect(() => {
        let isChecking = false;

        const checkSession = async () => {
            if (isChecking) return; // Previne múltiplas verificações simultâneas
            isChecking = true;

            try {
                logger.debug("Verificando sessão");
                const { data: { session }, error } = await supabase.auth.getSession();
                
                if (error) {
                    logger.error("Erro ao obter sessão:", error.message);
                    setIsLoggedIn(false);
                    setIsLoading(false);
                    return;
                }

                if (session) {
                    logger.debug("Sessão ativa encontrada");
                    setIsLoggedIn(true);
                    setUserId(session.user.id);
                    
                    // Busca dados do perfil
                    const { data: profileData, error: profileError } = await supabase
                        .from('profiles')
                        .select('role, regionals, active')
                        .eq('email', session.user.email)
                        .single();
                    
                    if (!profileError && profileData) {
                        // Verificar se o usuário está ativo
                        if (!profileData.active) {
                            logger.warn("Usuário inativo detectado na sessão");
                            await supabase.auth.signOut();
                            setIsLoggedIn(false);
                            setUserRole(null);
                            setUserRegionals([]);
                            setUserId(null);
                            return;
                        }

                        setUserRole(profileData.role);
                        setUserRegionals(profileData.regionals || []);
                        logger.debug("Perfil carregado com sucesso");
                    }
                } else {
                    logger.debug("Nenhuma sessão encontrada");
                    setIsLoggedIn(false);
                }
                
                setIsLoading(false);
            } catch (error) {
                logger.error("Erro na verificação de sessão:", error.message);
                setIsLoggedIn(false);
                setIsLoading(false);
            } finally {
                isChecking = false;
            }
        };

        // Verifica sessão apenas uma vez na montagem
        checkSession();

        // Escuta mudanças no estado de autenticação
        const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
            logger.debug("Auth state change:", event);
            
            if (event === 'SIGNED_IN' && session) {
                setIsLoggedIn(true);
                setUserId(session.user.id);
                setIsLoading(false);
            } else if (event === 'SIGNED_OUT') {
                setIsLoggedIn(false);
                setUserRole(null);
                setUserRegionals([]);
                setUserId(null);
                setIsLoading(false);
            }
        });

        return () => {
            if (subscription && typeof subscription === 'function') {
                subscription();
            }
        };
    }, []); // Dependências vazias - executa apenas uma vez

    // Mostra tela de carregamento enquanto verifica a sessão
    if (isLoading) {
        return (
            <div className="app-container">
                <div className="loading-overlay">
                    <div className="spinner"></div>
                    <div className="loading-text">Verificando autenticação...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="app-container">
            <Router>
                <div className="main-content">
                    <Routes>
                        <Route path="/login" element={<Login setIsLoggedIn={setIsLoggedIn} onLoginSuccess={handleLoginSuccess} />} />
                        <Route path="/postes" element={isLoggedIn ? <Postes userRole={userRole} userRegionals={userRegionals} onLogout={handleLogout} /> : <Navigate to="/login" />} />
                        <Route path="/historico" element={isLoggedIn ? <HistoryPage userRole={userRole} userRegionals={userRegionals} userId={userId} onLogout={handleLogout} /> : <Navigate to="/login" />} />
                        <Route path="/admin" element={isLoggedIn ? <AdminPage userRole={userRole} onLogout={handleLogout} /> : <Navigate to="/login" />} />
                        <Route path="/" element={<Navigate to="/login" />} />
                    </Routes>
                </div>

                <footer className="footer">
                    <p>Desenvolvido por T.I PE • Gerador de NS v2.0</p>
                </footer>
            </Router>
        </div>
    );
}

export default App;
